import React, { useEffect, useMemo, useState } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  MinusCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { formatDuration } from './VideoHeaderStrip';
import { formatFindingRange, formatFindingTime } from '../utils/findingTime';

/**
 * The one component every observation renders through, on both the report
 * (`pages/ReportPage.js`) and the review queue (`pages/ReviewQueuePage.js`) -- this
 * phase's brief. An "observation" is one `ReportObservation`
 * (`backend/app/schemas/reports.py`), the walkthrough record's actual unit: every shot
 * the pipeline formed, in time order, whether or not it became a finding.
 *
 * -------------------------------------------------------------------------------------
 * PROP CONTRACT
 * -------------------------------------------------------------------------------------
 * `observation` (required) is expected to carry exactly `ReportObservation`'s field
 * names, verbatim, since that is this codebase's one already-implemented, correct
 * source of the `outcome.code` classification (`report_service.py` computes it; this
 * component only *renders* it, it never re-derives "matched" vs "matched_disputed_kept"
 * vs "raised_then_withdrawn" from a raw Finding -- getting that reclassification right
 * belongs to the schema's one producer, not to a second, client-side guess):
 *
 *   {
 *     id?: string,                 // NOT part of ReportObservation -- see below
 *     sequence_no: number,
 *     group_id: string,
 *     time_range: { start_s: number|null, end_s: number|null },
 *     representative_frame_url: string|null,
 *     frame_urls: string[],
 *     disposition: 'finding' | 'no_issue',
 *     observed: object|null,       // describer's raw reply, or null if it never ran
 *     assessment: object|null,     // mapper's read, or null if it never ran
 *                                  //   { criterion_id, criterion_title, appraisal_ref,
 *                                  //     problem_identified, standard_suggestion,
 *                                  //     standard_body_excerpt, evidence_in_frame,
 *                                  //     confidence, rank }
 *     review: object|null,         // { verifier: {answer,reason,model_id}|null,
 *                                  //   tiebreaker: {keep,revised_problem,reason,model_id}|null }
 *     outcome: {
 *       code: 'matched'|'matched_disputed_kept'|'raised_then_withdrawn'|'below_cap'
 *             |'no_criterion'|'not_assessed'|'recurrence',
 *       label: string,
 *       status?: 'pending'|'approved'|'rejected',
 *       reason?: string,           // read only to pick a not_assessed sentence below;
 *                                  // never shown verbatim (reason_detail is worse: see
 *                                  // the INTERNAL-ONLY note below and this component
 *                                  // never reads it at all)
 *       auditor_notes?: string,
 *       recurrence_of?: {          // code === 'recurrence' only -- see RECURRENCE-
 *                                  // VISIBILITY below
 *         group_id: string|null, display_id: string, sequence_no: number|null,
 *         start_s: number|null, end_s: number|null,
 *       },
 *     },
 *   }
 *
 * -------------------------------------------------------------------------------------
 * RECURRENCE-VISIBILITY (this phase's brief, verbatim: "explicitly mention" a hazard
 * seen more than once -- never silently collapse it)
 * -------------------------------------------------------------------------------------
 * The backend's RECURRENCE stage merges repeat sightings of one physical hazard into a
 * single survivor `Finding`, folding every other sighting into that finding's own
 * `occurrences` (`app/schemas/findings.py`) rather than reporting it a second time. Two
 * things make that merge visible here, per Anshul's brief, rather than just tidier:
 *
 *   1. A merged survivor's own entry carries `assessment.recurrence` -- `null` for the
 *      ordinary, single-occurrence finding (must not regress: no recurrence language at
 *      all in that case) or, once merged,
 *      `{ times_seen, span_start_s, span_end_s, occurrences: [{start_s, end_s,
 *      representative_frame_url, frame_urls, confidence, group_id}, ...] }`.
 *      `AssessmentBlock` renders this as a plain-English sentence -- the count and span,
 *      built HERE from raw seconds via `formatDuration` (the backend deliberately ships
 *      structured seconds, not a pre-written sentence, so this file owns the one wording
 *      choice, same division of labour as every other templated line in this component)
 *      -- followed by one row per occurrence: its own timestamp and its own frame.
 *   2. Every folded-away pass ALSO still appears as its OWN entry in the walkthrough log,
 *      at its own shot time, with `outcome.code === 'recurrence'` and
 *      `outcome.recurrence_of` naming the survivor it repeats. It renders compact, like
 *      the other quiet codes (`quiet` below), in the SLATE register -- never green (that
 *      would read as "examined and clear", which this shot was not, in isolation, judged
 *      to be) and never amber/red (that would read as a fresh, separate finding, which is
 *      exactly the double-counting this whole stage exists to prevent). It carries no
 *      review controls under any circumstance (`canReview` below excludes this code by
 *      name, not only by `disposition`, since a caller could otherwise wire up
 *      `onStatusChange` for it): the decision belongs to the survivor entry, and offering
 *      approve/reject on a repeat would let an auditor contradict themselves on the same
 *      physical hazard.
 *
 * `id` is the one addition beyond `ReportObservation`: the underlying `Finding`'s
 * database UUID, needed so the review-action callbacks below know which finding a click
 * refers to. It is optional and only ever consulted for `disposition === 'finding'`
 * rows; every id fallback below (`observation.id ?? observation.group_id`) exists only
 * to keep this component crash-safe when it is missing, not because a group id is a
 * valid substitute for API calls -- a caller wiring up review actions on a finding-
 * backed entry MUST supply `id`.
 *
 * INTERNAL ONLY, never rendered here regardless of caller: `outcome.reason_detail` (raw
 * internal diagnostic string) and `outcome.answer_key_match` (iSafetyBench is CC
 * BY-NC-SA, non-commercial), `review.verifier.model_id` / `review.tiebreaker.model_id`,
 * and anything resembling `header.model_ids` / `usage` -- none of those are read by this
 * file at all, so a caller cannot accidentally leak them by passing them through. This
 * matters because the SAME component renders on the customer-facing report, where the
 * "what not to build" list forbids every one of them appearing.
 *
 * -------------------------------------------------------------------------------------
 * PAGE WIRING
 * -------------------------------------------------------------------------------------
 * `pages/ReportPage.js`: `report.observations` (`ReportResponse.observations`) is
 * already exactly this shape, one entry per array item, in order -- pass each straight
 * through as `observation`, no adaptation. Pass none of the review-control props (the
 * report is a settled record; this phase's brief: "The report passes none").
 *
 * `pages/ReviewQueuePage.js`: today's `GET .../findings` response has no `observations`
 * field (it hands back `findings[]`/`no_issue[]` in the older, pre-walkthrough shape --
 * `FindingListResponse`, `app/schemas/findings.py`). Wiring this page to
 * `ObservationEntry` needs one of the two ordered lists it already has adapted into the
 * shape above, in `sequence_no` (time) order, interleaving `findings` and `no_issue`.
 * That adaptation is deliberately NOT done in this file: the one non-trivial part of it
 * -- computing `outcome.code`/`outcome.label` for a live `Finding` row (matched vs.
 * matched_disputed_kept vs. raised_then_withdrawn vs. below_cap) -- is exactly the logic
 * `report_service.py` already owns and gets right today; re-deriving it a second time
 * client-side risks the two classifications drifting apart. The lower-risk integration
 * path is for the review queue to read the SAME `observations` list the report reads
 * (either by calling the report endpoint too, or by a follow-up backend change that adds
 * an `observations` field to the findings response) rather than reconstructing outcome
 * codes in the browser. Everything else in this shape -- frames, `observed`, `assessment`
 * -- is a direct rename of fields `FindingResponse`/`NoIssueGroup` already carry, so once
 * `outcome` is supplied correctly the rest of the adaptation is mechanical.
 *
 * Review-control props (all optional; rendered only when `observation.disposition ===
 * 'finding'` AND the corresponding callback is supplied -- the report calls this
 * component with none of them and gets no controls at all):
 *   criteria?: Array<{id, title}>       // for the criterion dropdown while editing
 *   criteriaAvailable?: boolean          // false -> read-only criterion label (403 case)
 *   isEditing?: boolean
 *   isSaving?: boolean
 *   isActive?: boolean                   // visual highlight, e.g. after a "Jump to"
 *   onStatusChange?: (id, status) => void        // status: pending|approved|rejected
 *   onEdit?: (id) => void
 *   onCancelEdit?: () => void
 *   onSave?: (id, patch) => void          // patch: subset of {criterion_id,
 *                                         // problem_identified, standard_suggestion,
 *                                         // auditor_notes} that actually changed
 *   onJumpTo?: (seconds, id) => void      // frame/thumbnail click -> video seek
 *
 * -------------------------------------------------------------------------------------
 * VISUAL REGISTERS (load-bearing, this phase's brief)
 * -------------------------------------------------------------------------------------
 * green  ONLY `outcome.code === 'no_criterion'` ("examined and clear").
 * slate  `outcome.code === 'not_assessed'` OR `'recurrence'` -- never inherits green just
 *        because the entry happens to be quiet; "not observed", "observed, nothing
 *        notable", and "observed, but it's the same hazard as another entry" are three
 *        different states (see `NoIssueGroup`'s own docstring on the first two; a
 *        `recurrence` entry shares slate with `not_assessed` deliberately -- it is
 *        neither a fresh finding nor an all-clear, so it must read as neither amber/red
 *        nor green, per this phase's brief).
 * amber/red every other code -- always a finding (or a former finding: below_cap,
 *        raised_then_withdrawn), never green, never slate: a matched hazard is never
 *        painted the same colour as "the site was clean here."
 *
 * -------------------------------------------------------------------------------------
 * COLLAPSE (this phase's brief, verbatim rules)
 * -------------------------------------------------------------------------------------
 * On screen: `matched` / `matched_disputed_kept` / `raised_then_withdrawn` / `below_cap`
 * always render their full body (frame strip, observed, assessment, outcome) -- no
 * disclosure, nothing to expand. `no_criterion` / `not_assessed` / `recurrence` start
 * collapsed to header + one thumbnail + the notable line, behind a "Show details"
 * disclosure -- a repeat sighting is exactly as quiet, on screen, as the other two.
 * In print: the full body for the quiet codes never appears (the disclosure content
 * carries `print:hidden` unconditionally, regardless of on-screen expanded state) --
 * "the quiet ones render compact" holds even if a reader had expanded one right before
 * printing. Every entry carries `break-inside-avoid` so a card that fits a page stays
 * whole; a full finding card is usually taller than one A4 page (QA-36 measured
 * 1621-2533 CSS px against 1017 printable), in which case Chrome must cut it somewhere,
 * so every section root (frame strip, observed, each assessment block, outcome) carries
 * its own `break-inside-avoid` to steer that cut BETWEEN sections rather than through an
 * image or a paragraph, and the thumbnail strip is `print:hidden` since its only job is
 * to switch the main frame, which paper cannot do.
 */

const FRAME_FILENAME_RE = /(\d{8})\.jpg(?:[?#]|$)/;

/** Mirrors `FindingCard.js`'s helper of the same name/behaviour (not imported: that
 * file exports no named helpers, only its default). Reads a frame's timestamp from the
 * URL the API already sent (`/media/runs/{video_id}/{run_id}/frames/{t_ms:08d}.jpg`). */
function frameSecondsFromUrl(url) {
  if (typeof url !== 'string') return null;
  const match = url.match(FRAME_FILENAME_RE);
  if (!match) return null;
  return Number(match[1]) / 1000;
}

// `kind` (a video's `kind` field) is optional and defaults to the ordinary mm:ss/h:mm:ss
// reading via `formatFindingRange`; only a caller that knows it is rendering a photo set's
// own start_s/end_s (a 1-based photo number, per Anshul 2026-09-26) passes `'photos'`.
function formatRange(startS, endS, kind) {
  return formatFindingRange(startS, endS, kind);
}

function confidencePct(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) return null;
  return Math.round(value * 100);
}

function confidenceBarClass(value) {
  if (value >= 0.7) return 'bg-green-500';
  if (value >= 0.4) return 'bg-yellow-500';
  return 'bg-red-500';
}

function confidenceLevel(value) {
  if (value >= 0.7) return 'High';
  if (value >= 0.4) return 'Medium';
  return 'Low';
}

function ConfidenceBar({ value }) {
  if (typeof value !== 'number' || Number.isNaN(value)) return null;
  const pct = confidencePct(value);
  return (
    <div className="break-inside-avoid">
      <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
        <span>Confidence · {confidenceLevel(value)}</span>
        <span className="font-medium text-gray-700">{pct}%</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className={`h-2 rounded-full transition-all duration-500 ${confidenceBarClass(value)}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/** One entry of `observed.objects[]`, rendered defensively -- ground truth (real runs)
 * has produced objects shaped `{name, count, condition}` carrying ad hoc extras
 * (`name2`, `count_note`) the mapper never declared. Every key actually present is
 * folded into the label; a wholly unrecognised shape still renders its raw key: value
 * pairs rather than vanishing. */
function describeObject(obj) {
  if (obj == null) return null;
  if (typeof obj !== 'object') return String(obj);
  const bits = [];
  if (obj.name) bits.push(String(obj.name));
  if (obj.name2 && obj.name2 !== obj.name) bits.push(`/ ${obj.name2}`);
  if (obj.count !== undefined && obj.count !== null) bits.push(`×${obj.count}`);
  if (obj.count_note) bits.push(`(${obj.count_note})`);
  if (obj.condition) bits.push(`— ${obj.condition}`);
  if (bits.length > 0) return bits.join(' ');
  const fallback = Object.entries(obj)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => `${k}: ${v}`)
    .join(', ');
  return fallback || null;
}

function ChipList({ items, tone = 'bg-gray-100 text-gray-700' }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((item, idx) => {
        const label = typeof item === 'string' ? item : describeObject(item);
        if (!label) return null;
        return (
          <span
            key={idx}
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${tone}`}
          >
            {label}
          </span>
        );
      })}
    </div>
  );
}

function safeStringify(value) {
  try {
    return JSON.stringify(value);
  } catch (err) {
    return String(value);
  }
}

/** "What the model saw" (render order item 3) -- IDENTICAL markup regardless of
 * `disposition`, per this phase's brief: a `no_issue` group's `observed` block (from
 * `NoIssueGroup.analysis.description`) goes through the exact same rendering path as a
 * `finding`'s (`Finding.description`). Defensive by construction: never assumes
 * `objects`/`people`/`environment`/`notable` exist, and never drops a top-level key it
 * did not expect -- folded into an "Additional detail" list instead of vanishing. */
function ObservedBlock({ observed }) {
  if (!observed || typeof observed !== 'object') {
    return (
      <div className="break-inside-avoid">
        <SectionLabel>What the model saw</SectionLabel>
        <p className="text-sm text-gray-400 italic">No description recorded for this shot.</p>
      </div>
    );
  }

  const { objects, people, environment, notable, ...rest } = observed;
  const objectList = Array.isArray(objects) ? objects : [];
  const peopleObj = people && typeof people === 'object' ? people : null;
  const environmentObj = environment && typeof environment === 'object' ? environment : null;
  const notableText = typeof notable === 'string' ? notable : null;

  const scalarExtras = Object.entries(rest).filter(
    ([, v]) => v !== null && v !== undefined && v !== '' && typeof v !== 'object'
  );
  const objectExtras = Object.entries(rest).filter(([, v]) => v !== null && typeof v === 'object');

  const hasAnything =
    notableText ||
    objectList.length > 0 ||
    peopleObj ||
    environmentObj ||
    scalarExtras.length > 0 ||
    objectExtras.length > 0;

  if (!hasAnything) {
    return (
      <div className="break-inside-avoid">
        <SectionLabel>What the model saw</SectionLabel>
        <p className="text-sm text-gray-400 italic">No description recorded for this shot.</p>
      </div>
    );
  }

  return (
    <div className="break-inside-avoid">
      <SectionLabel>What the model saw</SectionLabel>
      <div className="space-y-3">
        {notableText && (
          <p className="text-sm text-gray-800 italic border-l-2 border-blue-200 pl-3">“{notableText}”</p>
        )}
        {objectList.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Objects seen</p>
            <ChipList items={objectList} />
          </div>
        )}
        {peopleObj && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">People</p>
            <p className="text-sm text-gray-700">
              {peopleObj.count !== undefined && peopleObj.count !== null
                ? `Count: ${peopleObj.count}`
                : 'Count unknown'}
              {peopleObj.activity ? ` · ${peopleObj.activity}` : ''}
            </p>
            {Array.isArray(peopleObj.ppe_visible) && peopleObj.ppe_visible.length > 0 && (
              <div className="mt-1">
                <ChipList items={peopleObj.ppe_visible} tone="bg-teal-50 text-teal-800" />
              </div>
            )}
          </div>
        )}
        {environmentObj && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Environment</p>
            <p className="text-sm text-gray-700">
              {[environmentObj.setting, environmentObj.lighting, environmentObj.surfaces]
                .filter(Boolean)
                .join(' · ') || 'Not described'}
            </p>
            {Array.isArray(environmentObj.hazards_visible) && environmentObj.hazards_visible.length > 0 && (
              <div className="mt-1">
                <ChipList items={environmentObj.hazards_visible} tone="bg-red-50 text-red-700" />
              </div>
            )}
          </div>
        )}
        {(scalarExtras.length > 0 || objectExtras.length > 0) && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Additional detail</p>
            <ul className="text-sm text-gray-600 space-y-0.5">
              {scalarExtras.map(([k, v]) => (
                <li key={k}>
                  {k}: {String(v)}
                </li>
              ))}
              {objectExtras.map(([k, v]) => (
                <li key={k}>
                  {k}: {safeStringify(v)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

/** "What was said" (RUNLOG V6): the lines spoken during this shot, from the run's
 * transcript (`observation.speech`, built by `report_service._attach_speech`) -- the same
 * lines the mapper read beside the frame. Rendered only when there is at least one line:
 * `speech` is `null` for a run with no transcript and `[]` for a shot where nobody spoke,
 * and neither needs a section of its own on every entry. The English line appears only
 * when the corrector produced one that differs from the spoken text. */
function SpeechBlock({ speech }) {
  if (!Array.isArray(speech) || speech.length === 0) return null;
  return (
    <div className="break-inside-avoid" data-testid="speech-block">
      <SectionLabel>What was said</SectionLabel>
      <ul className="space-y-2">
        {speech.map((line, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={i} className="text-sm border-l-2 border-purple-200 pl-3">
            <p className="text-xs text-gray-500">
              {formatRange(line.start_s, line.end_s)} · {line.speaker || 'Speaker'}
            </p>
            <p className="text-gray-800">{line.text}</p>
            {line.english && <p className="text-gray-500 italic">{line.english}</p>}
            {Array.isArray(line.unsure) && line.unsure.length > 0 && (
              <p className="text-xs text-amber-700">Unclear in the recording: {line.unsure.join(', ')}</p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The disagreement path, visual markup copied from `FindingCard.js`'s own
 * `DisagreementPanel` (that file exports no named helpers, so it is reproduced here
 * rather than imported -- the brief's "reuse its markup", not its module). Takes the
 * `review` shape (`{verifier, tiebreaker}`) rather than a bare `finding`, since a
 * no-issue group's disagreement (`NoIssueGroup.analysis.verifier/.tiebreaker`, e.g. a
 * `tiebreaker_dropped` group) renders through the identical path. `model_id` on either
 * verdict is deliberately never read: internal-only, matching `FindingCard`'s own
 * choice and this phase's "what not to build" list. */
function DisagreementPanel({ review }) {
  const verifier = review && review.verifier;
  const tiebreaker = review && review.tiebreaker;
  if (!verifier || verifier.answer !== 'no') return null;
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-3 break-inside-avoid">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-amber-800 mb-1">Verifier objected</p>
        <p className="text-sm text-amber-900">{verifier.reason || 'No reason recorded.'}</p>
      </div>
      {tiebreaker ? (
        <div className="pt-3 border-t border-amber-200">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-800 mb-1">
            Tiebreaker review — {tiebreaker.keep ? 'kept the finding' : 'judged it not a finding'}
          </p>
          <p className="text-sm text-amber-900 mb-2">{tiebreaker.reason || 'No reason recorded.'}</p>
          {tiebreaker.revised_problem && (
            <div className="bg-white border border-amber-200 rounded-md p-3">
              <p className="text-xs font-semibold text-gray-500 mb-1">
                Rewritten problem statement — this is what the auditor reads below
              </p>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{tiebreaker.revised_problem}</p>
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-amber-700 italic">No tiebreaker has reviewed this objection yet.</p>
      )}
    </div>
  );
}

function SectionLabel({ children }) {
  return <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">{children}</p>;
}

/** THE VISUAL REGISTERS (this phase's brief, load-bearing): green only for
 * `no_criterion`, slate for `not_assessed` and `recurrence`, amber/red for every other
 * code -- a matched hazard (or a former one: below_cap, raised_then_withdrawn) is never
 * painted the colour of "examined and clear" or "not assessed yet", and a repeat sighting
 * (RECURRENCE-VISIBILITY ADDITION, see this file's module docstring) is never painted the
 * colour of either a fresh finding or an all-clear. */
function registerFor(code) {
  if (code === 'no_criterion') return 'green';
  if (code === 'not_assessed' || code === 'recurrence') return 'slate';
  if (code === 'raised_then_withdrawn') return 'red';
  return 'amber'; // matched, matched_disputed_kept, below_cap
}

const REGISTER_STYLES = {
  green: 'bg-green-100 text-green-800 border border-green-200',
  amber: 'bg-amber-100 text-amber-900 border border-amber-200',
  red: 'bg-red-100 text-red-800 border border-red-200',
  slate: 'bg-slate-100 text-slate-600 border border-slate-200',
};

function OutcomeBadge({ label, register }) {
  return (
    <span
      data-testid="outcome-badge"
      data-register={register}
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        REGISTER_STYLES[register] || REGISTER_STYLES.slate
      }`}
    >
      {label || '—'}
    </span>
  );
}

function EntryHeader({ observation, register, videoKind }) {
  const seq = observation.sequence_no;
  const start = observation.time_range && observation.time_range.start_s;
  const end = observation.time_range && observation.time_range.end_s;
  return (
    <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
      <div className="flex items-center gap-2 flex-wrap">
        {/* QA-46: axe color-contrast 2.53:1 on gray-400; this label is what the
            recurrence text points at ("Same hazard as finding #5"), so it must be
            readable, not decorative. gray-600 clears 4.5:1 on white and on a light
            grey print stock. */}
        <span className="text-xs font-mono text-gray-600">
          #{seq !== undefined && seq !== null ? seq : '—'}
        </span>
        <span className="text-sm text-gray-600">{formatRange(start, end, videoKind)}</span>
      </div>
      <OutcomeBadge label={observation.outcome && observation.outcome.label} register={register} />
    </div>
  );
}

/** One `recurrence_of` reference (survivor group id, display id, sequence number, own
 * time range) rendered as the short phrase every recurrence-facing message below builds
 * on -- "finding #<n>" when a sequence number is known (the common case, since the
 * survivor is always in the same `observations` list), falling back to its display id
 * otherwise so this never renders a bare "another finding" when better data exists. */
function primaryReference(recurrenceOf) {
  const primary = recurrenceOf || {};
  if (primary.sequence_no !== null && primary.sequence_no !== undefined) {
    return `finding #${primary.sequence_no}`;
  }
  if (primary.display_id) return `finding ${primary.display_id}`;
  return 'another finding';
}

/** The collapsed-by-default view for a quiet entry (`no_criterion` / `not_assessed` /
 * `recurrence`): header (rendered by the caller) plus one thumbnail plus the notable
 * line -- exactly this phase's brief, nothing more. Falls back to a templated,
 * non-fabricated sentence when there is no notable line, carefully NOT collapsing "the
 * describer never ran" into "it looked and saw nothing" (the invariant
 * `NoIssueGroup.analysis` exists to hold -- see this file's module docstring), and (this
 * phase's addition) NOT collapsing "this pass was never independently assessed" into
 * either of those for a recurrence entry -- it was assessed, on the finding it repeats. */
function compactFallbackText(observation) {
  const outcome = observation.outcome || {};
  if (outcome.code === 'recurrence') {
    return `Same hazard as ${primaryReference(outcome.recurrence_of)} — see that entry for the full assessment.`;
  }
  if (observation.observed === null || observation.observed === undefined) {
    return 'The model did not describe this shot.';
  }
  if (outcome.code === 'no_criterion') {
    return 'Described; nothing here maps to a GRIHA v6.0 criterion.';
  }
  return 'No summary line recorded for this shot.';
}

function CompactPreview({ observation, onJump }) {
  const rep = observation.representative_frame_url;
  const notable =
    observation.observed && typeof observation.observed.notable === 'string'
      ? observation.observed.notable
      : null;
  const seconds = frameSecondsFromUrl(rep);
  const clickable = typeof onJump === 'function' && seconds !== null;

  // QA-46: every other frame image names its timestamp; a screen reader hearing
  // "Representative frame" on a third of the queue's images gets nothing to place it by.
  const alt = seconds !== null ? `Frame at ${formatDuration(seconds)}` : 'Representative frame';
  const thumb = rep ? (
    <img src={rep} alt={alt} className="w-full h-full object-cover" loading="lazy" />
  ) : (
    <div className="w-full h-full flex items-center justify-center text-[10px] text-gray-400">No frame</div>
  );

  return (
    <div className="flex gap-3 items-start">
      {clickable ? (
        <button
          type="button"
          onClick={() => onJump(seconds)}
          className="w-20 h-14 flex-shrink-0 rounded-md border border-gray-200 overflow-hidden bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {thumb}
        </button>
      ) : (
        <div className="w-20 h-14 flex-shrink-0 rounded-md border border-gray-200 overflow-hidden bg-gray-100">
          {thumb}
        </div>
      )}
      <p className="text-sm text-gray-600 italic min-w-0">{notable || compactFallbackText(observation)}</p>
    </div>
  );
}

/** Render order item 2 -- representative frame first, then every other frame the
 * group's own `frame_urls` list carries. Identical shape/behaviour whether the
 * observation is a finding or a no-issue group. */
function FrameStrip({ frames, activeIndex, onSelect, onJump }) {
  const activeUrl = frames[activeIndex] || null;
  const activeSeconds = frameSecondsFromUrl(activeUrl);
  // `onJump` is `undefined` when the caller (ObservationEntry) was given no `onJumpTo` --
  // ShotLog is exactly this case. Rendered as a plain, non-interactive frame then, rather
  // than a button whose click would silently do nothing (2026-09-06 standing rule).
  const clickable = typeof onJump === 'function' && activeSeconds !== null;
  const image = (
    <img
      src={activeUrl}
      alt={`Frame at ${formatDuration(activeSeconds)}`}
      className="w-full h-40 object-cover"
      loading="lazy"
    />
  );

  if (frames.length === 0) {
    return (
      <div className="h-40 rounded-md border border-dashed border-gray-300 flex items-center justify-center text-xs text-gray-400">
        No frames captured
      </div>
    );
  }

  return (
    <div className="break-inside-avoid">
      {clickable ? (
        <button
          type="button"
          onClick={() => onJump(activeSeconds)}
          className="block w-full max-w-xs rounded-md border border-gray-200 overflow-hidden bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {image}
        </button>
      ) : (
        <div className="block w-full max-w-xs rounded-md border border-gray-200 overflow-hidden bg-gray-100">
          {image}
        </div>
      )}
      {frames.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1 mt-2 max-w-xs print:hidden">
          {frames.map((url, idx) => {
            const secs = frameSecondsFromUrl(url);
            const active = idx === activeIndex;
            return (
              <button
                key={url + idx}
                type="button"
                onClick={() => onSelect(idx)}
                className={`flex-shrink-0 rounded-md border overflow-hidden focus:ring-2 focus:ring-blue-500 ${
                  active ? 'border-blue-500 ring-1 ring-blue-300' : 'border-gray-200 hover:border-gray-400'
                }`}
              >
                <img
                  src={url}
                  alt={`Frame at ${formatDuration(secs)}`}
                  className="h-12 w-20 object-cover bg-gray-100"
                  loading="lazy"
                />
                <div className="text-[10px] text-gray-500 bg-[#F8F8FF] text-center">{formatDuration(secs)}</div>
              </button>
            );
          })}
        </div>
      )}
      <p className="mt-1.5 text-xs text-gray-500">
        {frames.length} frame{frames.length === 1 ? '' : 's'} the model looked at
      </p>
    </div>
  );
}

const NOT_ASSESSED_REASON_TEXT = {
  describer_failed: 'The model could not describe this shot.',
  mapper_failed: 'The model could not complete its assessment of this shot.',
  budget_exceeded: 'Skipped — the run reached its time budget before this shot was assessed.',
};

function notAssessedText(outcome) {
  const reason = outcome && outcome.reason;
  if (reason && NOT_ASSESSED_REASON_TEXT[reason]) return NOT_ASSESSED_REASON_TEXT[reason];
  if (outcome && outcome.status === 'pending') return 'This finding has not been reviewed yet.';
  return 'Not assessed yet.';
}

/** The count-and-span sentence for a merged finding's `assessment.recurrence` -- built
 * HERE, from the raw `times_seen`/`span_start_s`/`span_end_s` seconds the backend ships,
 * via this file's own `formatDuration` (see this file's module docstring's RECURRENCE-
 * VISIBILITY section for why the backend deliberately does not pre-write this sentence).
 * Wording chosen to read as something a person would say, not a field dump: "seen twice"
 * rather than "times_seen: 2", "between X and Y" rather than a raw span. */
function recurrenceSentence(timesSeen, spanStartS, spanEndS, kind) {
  const timesPhrase = timesSeen === 2 ? 'twice' : `${timesSeen} times`;
  // Wording for every non-photo kind is unchanged, verbatim (pinned by this file's own
  // tests); only a photo set gets its own noun for the place the hazard recurred in.
  const place = kind === 'photos' ? 'this photo set' : 'this walkthrough';
  if (spanStartS === null || spanStartS === undefined) {
    return `This hazard was seen ${timesPhrase} during ${place}.`;
  }
  if (spanEndS === null || spanEndS === undefined || spanEndS === spanStartS) {
    return `This hazard was seen ${timesPhrase} during ${place}, first at ${formatFindingTime(spanStartS, kind)}.`;
  }
  const joiner = timesSeen === 2 ? 'at' : 'between';
  return `This hazard was seen ${timesPhrase} during ${place}, ${joiner} ${formatFindingTime(
    spanStartS,
    kind
  )} and ${formatFindingTime(spanEndS, kind)}.`;
}

/** One row of a merged finding's occurrence list -- its own timestamp and its own frame,
 * clickable to jump the video there, per this phase's brief: "an auditor must be able to
 * see each pass ... and check the merge was right". */
function RecurrenceOccurrenceRow({ occurrence, onJump, videoKind }) {
  const seconds = occurrence.start_s;
  const clickable = typeof onJump === 'function' && seconds !== null && seconds !== undefined;
  const thumb = occurrence.representative_frame_url ? (
    <img
      src={occurrence.representative_frame_url}
      alt={`Frame at ${formatFindingTime(seconds, videoKind)}`}
      className="w-full h-full object-cover"
      loading="lazy"
    />
  ) : (
    <div className="w-full h-full flex items-center justify-center text-[9px] text-gray-400">No frame</div>
  );
  const confPct = confidencePct(occurrence.confidence);

  return (
    <div className="flex items-center gap-2.5">
      {clickable ? (
        <button
          type="button"
          onClick={() => onJump(seconds)}
          className="w-14 h-10 flex-shrink-0 rounded border border-gray-200 overflow-hidden bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {thumb}
        </button>
      ) : (
        <div className="w-14 h-10 flex-shrink-0 rounded border border-gray-200 overflow-hidden bg-gray-100">
          {thumb}
        </div>
      )}
      <span className="text-sm text-gray-700">
        {formatRange(occurrence.start_s, occurrence.end_s, videoKind)}
        {confPct !== null ? ` · ${confPct}% confidence` : ''}
      </span>
    </div>
  );
}

/** "Seen more than once" panel on a merged finding's own entry -- the plain-language
 * statement plus the per-occurrence list this phase's brief asks for. Renders nothing
 * (not even an empty box) when `recurrence` is absent, which is the ordinary,
 * single-occurrence case and MUST show no recurrence language at all. */
function RecurrenceBlock({ recurrence, onJump, videoKind }) {
  if (!recurrence) return null;
  const { times_seen: timesSeen, span_start_s: spanStartS, span_end_s: spanEndS, occurrences } = recurrence;
  const occurrenceList = Array.isArray(occurrences) ? occurrences : [];
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2.5 break-inside-avoid">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">Seen more than once</p>
      <p className="text-sm text-slate-800">{recurrenceSentence(timesSeen, spanStartS, spanEndS, videoKind)}</p>
      {occurrenceList.length > 0 && (
        <div className="space-y-2 pt-1">
          {occurrenceList.map((occurrence, idx) => (
            <RecurrenceOccurrenceRow
              key={occurrence.group_id || idx}
              occurrence={occurrence}
              onJump={onJump}
              videoKind={videoKind}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Render order item 4, branching by `outcome.code` per this phase's brief. The
 * `no_criterion` sentence is the ONE FIXED SENTENCE this codebase allows itself here
 * (computed, not model-written -- see the module's "what not to build" note); every
 * other branch either shows the mapper's real analysis (a Finding's fields, whether it
 * is still a live finding or a dropped/capped one) or a templated not-assessed line.
 * `recurrence` (RECURRENCE-VISIBILITY ADDITION) names the survivor this pass repeats
 * rather than repeating an assessment this pass never independently received. */
function AssessmentBlock({ observation, onJump, videoKind }) {
  const outcome = observation.outcome || {};
  const code = outcome.code;
  const assessment = observation.assessment;

  if (code === 'recurrence') {
    const primary = outcome.recurrence_of || {};
    const when =
      primary.start_s !== null && primary.start_s !== undefined
        ? ` (${formatRange(primary.start_s, primary.end_s, videoKind)})`
        : '';
    return (
      <div>
        <SectionLabel>Assessment</SectionLabel>
        <p className="text-sm text-gray-700">
          This is the same physical hazard as {primaryReference(outcome.recurrence_of)}
          {when}. See that entry for the full assessment and occurrence list.
        </p>
      </div>
    );
  }

  if (code === 'no_criterion') {
    return (
      <div>
        <SectionLabel>Assessment</SectionLabel>
        <p className="text-sm text-gray-700">
          Assessed against the GRIHA v6.0 criteria pack. No criterion applies to what is visible here.
        </p>
      </div>
    );
  }

  if (code === 'not_assessed') {
    return (
      <div>
        <SectionLabel>Assessment</SectionLabel>
        <p className="text-sm text-gray-500 italic">{notAssessedText(outcome)}</p>
      </div>
    );
  }

  if (!assessment || typeof assessment !== 'object') {
    return (
      <div>
        <SectionLabel>Assessment</SectionLabel>
        <p className="text-sm text-gray-400 italic">No assessment recorded for this shot.</p>
      </div>
    );
  }

  // QA-36: each block below is its own `break-inside-avoid` island so a printed page
  // ends between blocks, never through a paragraph or the standard excerpt.
  return (
    <div className="space-y-3">
      <SectionLabel>Assessment</SectionLabel>
      <div className="flex items-center flex-wrap gap-2 break-inside-avoid">
        {assessment.criterion_id && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 border border-blue-200 text-blue-800">
            {assessment.criterion_id}
          </span>
        )}
        {(assessment.criterion_title || assessment.criterion_id) && (
          <h4 className="text-sm font-semibold text-gray-900">
            {assessment.criterion_title || assessment.criterion_id}
          </h4>
        )}
      </div>
      {code === 'below_cap' && (
        <p className="text-xs text-gray-500 italic break-inside-avoid">Ranked below the cap applied to this run.</p>
      )}
      {code === 'raised_then_withdrawn' && (
        <p className="text-xs text-red-700 italic break-inside-avoid">
          Raised by the pipeline, then withdrawn on review.
        </p>
      )}
      {assessment.problem_identified && (
        <div className="break-inside-avoid">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 mb-1">Problem identified</p>
          <p className="text-sm text-gray-800 whitespace-pre-wrap">{assessment.problem_identified}</p>
        </div>
      )}
      {assessment.standard_suggestion && (
        <div className="break-inside-avoid">
          <p className="text-xs font-semibold uppercase tracking-wide text-green-700 mb-1">Suggested fix</p>
          <p className="text-sm text-gray-800 whitespace-pre-wrap">{assessment.standard_suggestion}</p>
        </div>
      )}
      {assessment.standard_body_excerpt && (
        <div className="break-inside-avoid">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Standard</p>
          <pre className="whitespace-pre-wrap font-sans text-xs text-gray-600 bg-[#F8F8FF] border border-gray-200 rounded-md p-3">
            {assessment.standard_body_excerpt}
          </pre>
        </div>
      )}
      {Array.isArray(assessment.evidence_in_frame) && assessment.evidence_in_frame.length > 0 && (
        <div className="break-inside-avoid">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Evidence in frame</p>
          <ul className="list-disc list-inside text-sm text-gray-700 space-y-0.5">
            {assessment.evidence_in_frame.map((line, idx) => (
              <li key={idx}>{line}</li>
            ))}
          </ul>
        </div>
      )}
      <ConfidenceBar value={assessment.confidence} />
      <RecurrenceBlock recurrence={assessment.recurrence} onJump={onJump} videoKind={videoKind} />
      <DisagreementPanel review={observation.review} />
    </div>
  );
}

const REVIEW_STATUS_STYLES = {
  approved: { pill: 'bg-green-50 text-green-800 border-green-200', Icon: CheckCircleIcon, label: 'Approved' },
  rejected: { pill: 'bg-red-50 text-red-800 border-red-200', Icon: XCircleIcon, label: 'Rejected' },
  pending: { pill: 'bg-gray-50 text-gray-600 border-gray-200', Icon: MinusCircleIcon, label: 'Pending review' },
};

function ReviewStatusPill({ status }) {
  if (!status) return null;
  const style = REVIEW_STATUS_STYLES[status] || REVIEW_STATUS_STYLES.pending;
  const { Icon } = style;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${style.pill}`}
    >
      <Icon className="h-3.5 w-3.5" /> {style.label}
    </span>
  );
}

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

/** QA-45: never `disabled` while a save is in flight -- Chrome drops focus from a
 * disabled element to <body>, so a keyboard user who changed the status had to Tab from
 * the top of the page again (41 presses on clip-02) to reach the next card. The select
 * stays focusable, announces `aria-busy`, dims, and swallows a change made mid-save; the
 * controlled `value` then snaps the box back to the persisted status. */
function ReviewStatusSelect({ value, onChange, isSaving }) {
  return (
    <select
      aria-label="Review status"
      aria-busy={isSaving}
      value={value || 'pending'}
      onChange={(event) => {
        if (isSaving) return;
        onChange(event.target.value);
      }}
      className={`py-1.5 pl-2.5 pr-8 rounded-md border text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white border-gray-300 ${
        isSaving ? 'opacity-60' : ''
      }`}
    >
      {STATUS_OPTIONS.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

const EDITABLE_FIELDS = ['criterion_id', 'problem_identified', 'standard_suggestion', 'auditor_notes'];

/** Edit form for criterion / problem / suggestion / notes, seeded from the
 * observation's own `assessment`/`outcome.auditor_notes` -- same fields, same
 * change-detection convention as `FindingCard.js`'s `FindingEditForm` (Save with
 * nothing changed behaves like Cancel), reproduced rather than imported for the same
 * reason `DisagreementPanel` above is. */
function EditForm({ observation, entryId, criteria, criteriaAvailable, isSaving, onSave, onCancel }) {
  const assessment = observation.assessment || {};
  const outcome = observation.outcome || {};
  const [criterionId, setCriterionId] = useState(assessment.criterion_id || '');
  const [problem, setProblem] = useState(assessment.problem_identified || '');
  const [suggestion, setSuggestion] = useState(assessment.standard_suggestion || '');
  const [notes, setNotes] = useState(outcome.auditor_notes || '');

  useEffect(() => {
    setCriterionId(assessment.criterion_id || '');
    setProblem(assessment.problem_identified || '');
    setSuggestion(assessment.standard_suggestion || '');
    setNotes(outcome.auditor_notes || '');
    // Re-seed only when a different entry starts editing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryId]);

  const handleSave = () => {
    const before = {
      criterion_id: assessment.criterion_id || '',
      problem_identified: assessment.problem_identified || '',
      standard_suggestion: assessment.standard_suggestion || '',
      auditor_notes: outcome.auditor_notes || '',
    };
    const next = {
      criterion_id: criterionId,
      problem_identified: problem,
      standard_suggestion: suggestion,
      auditor_notes: notes,
    };
    const patch = {};
    EDITABLE_FIELDS.forEach((key) => {
      if ((before[key] || '') !== (next[key] || '')) patch[key] = next[key];
    });
    if (Object.keys(patch).length === 0) {
      onCancel();
      return;
    }
    onSave(patch);
  };

  const textareaClass =
    'block w-full border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
  const fieldId = (name) => `observation-${entryId}-${name}`;

  return (
    <div className="space-y-3">
      <div>
        <label
          htmlFor={fieldId('criterion')}
          className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1 block"
        >
          Criterion
        </label>
        {criteriaAvailable && Array.isArray(criteria) && criteria.length > 0 ? (
          <select
            id={fieldId('criterion')}
            value={criterionId}
            onChange={(event) => setCriterionId(event.target.value)}
            disabled={isSaving}
            className="py-2 pl-3 pr-8 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white w-full disabled:opacity-60"
          >
            {criteria.map((c) => (
              <option key={c.id} value={c.id}>
                {c.id} · {c.title}
              </option>
            ))}
          </select>
        ) : (
          <p id={fieldId('criterion')} className="text-sm text-gray-500">
            {criterionId || 'Unassigned'}{' '}
            <span className="text-xs text-gray-400">(criterion list unavailable to this account)</span>
          </p>
        )}
      </div>
      <div>
        <label
          htmlFor={fieldId('problem')}
          className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1 block"
        >
          Problem identified
        </label>
        <textarea
          id={fieldId('problem')}
          rows={3}
          value={problem}
          disabled={isSaving}
          onChange={(event) => setProblem(event.target.value)}
          className={textareaClass}
        />
      </div>
      <div>
        <label
          htmlFor={fieldId('suggestion')}
          className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1 block"
        >
          Suggested fix
        </label>
        <textarea
          id={fieldId('suggestion')}
          rows={3}
          value={suggestion}
          disabled={isSaving}
          onChange={(event) => setSuggestion(event.target.value)}
          className={textareaClass}
        />
      </div>
      <div>
        <label
          htmlFor={fieldId('notes')}
          className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1 block"
        >
          Auditor notes
        </label>
        <textarea
          id={fieldId('notes')}
          rows={2}
          value={notes}
          disabled={isSaving}
          onChange={(event) => setNotes(event.target.value)}
          className={textareaClass}
          placeholder="Optional notes for this review"
        />
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSaving}
          className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-60"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="inline-flex items-center px-4 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60"
        >
          {isSaving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

/** Render order item 5: badge, review status, auditor notes -- always last, per this
 * phase's brief ("whether it matched a criterion is the LAST LINE of each entry, not
 * the reason the entry exists"). Review controls (approve/reject/edit/reset) render
 * only when `disposition === 'finding'` AND the caller actually supplied the
 * callbacks -- the report calls this component with none of them. */
function OutcomeSection({
  observation,
  entryId,
  canReview,
  isEditing,
  isSaving,
  criteria,
  criteriaAvailable,
  onStatusChange,
  onEdit,
  onCancelEdit,
  onSave,
}) {
  const outcome = observation.outcome || {};
  const register = registerFor(outcome.code);

  return (
    <div className="pt-3 border-t border-gray-200 space-y-3 break-inside-avoid">
      <SectionLabel>Outcome</SectionLabel>
      <div className="flex items-center flex-wrap gap-2">
        <OutcomeBadge label={outcome.label} register={register} />
        <ReviewStatusPill status={outcome.status} />
      </div>
      {outcome.auditor_notes && (
        <div className="bg-[#F8F8FF] border border-gray-200 rounded-md p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Auditor notes</p>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{outcome.auditor_notes}</p>
        </div>
      )}
      {canReview &&
        (isEditing ? (
          <EditForm
            observation={observation}
            entryId={entryId}
            criteria={criteria}
            criteriaAvailable={criteriaAvailable}
            isSaving={isSaving}
            onSave={(patch) => onSave(entryId, patch)}
            onCancel={onCancelEdit}
          />
        ) : (
          <div className="flex items-center justify-between flex-wrap gap-2">
            <ReviewStatusSelect
              value={outcome.status}
              isSaving={isSaving}
              onChange={(status) => onStatusChange(entryId, status)}
            />
            <button
              type="button"
              onClick={() => onEdit(entryId)}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
            >
              <PencilSquareIcon className="h-4 w-4 mr-1.5" />
              Edit
            </button>
          </div>
        ))}
    </div>
  );
}

export default function ObservationEntry({
  observation,
  criteria = [],
  criteriaAvailable = true,
  isEditing = false,
  isSaving = false,
  isActive = false,
  onStatusChange,
  onEdit,
  onCancelEdit,
  onSave,
  onJumpTo,
  // The video's own `kind` (Anshul, 2026-09-26): a photo set's timestamps are photo
  // numbers, not seconds, and every place in this file that renders a finding's own
  // start_s/end_s (never a frame-filename-derived second) reads this to say "Photo N"
  // instead of a fabricated mm:ss. Optional and defaults to the ordinary video reading.
  videoKind,
}) {
  const obs = observation || {};
  const outcome = obs.outcome || {};
  const code = outcome.code;
  const quiet = code === 'no_criterion' || code === 'not_assessed' || code === 'recurrence';
  const entryKey = obs.id || obs.group_id || null;

  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    setExpanded(false);
  }, [entryKey]);

  const [activeFrameIndex, setActiveFrameIndex] = useState(0);
  useEffect(() => {
    setActiveFrameIndex(0);
  }, [entryKey]);

  const frameUrls = useMemo(
    () => (Array.isArray(obs.frame_urls) ? obs.frame_urls.filter(Boolean) : []),
    [obs.frame_urls]
  );
  const orderedFrames = useMemo(() => {
    const rep = obs.representative_frame_url;
    if (!rep) return frameUrls;
    const rest = frameUrls.filter((u) => u !== rep);
    return [rep, ...rest];
  }, [frameUrls, obs.representative_frame_url]);

  // A stable function reference ONLY when a caller actually wired a handler (e.g.
  // ReviewQueuePage's own seek). `undefined` otherwise -- ShotLog (VideoOverviewPage.js)
  // renders this component with no `onJumpTo` at all -- so every place below that takes
  // this as its own `onJump` prop (FrameStrip's main frame, CompactPreview,
  // RecurrenceOccurrenceRow) can tell a real handler apart from none and render a plain,
  // non-interactive element instead of a button that silently does nothing (2026-09-06
  // standing rule: "no button that does nothing"). Checking `typeof onJump === 'function'`
  // on THIS wrapper would always be true regardless of whether `onJumpTo` was supplied,
  // which is exactly the bug: it made every one of those elements a button unconditionally.
  const canJump = typeof onJumpTo === 'function';
  const jump = canJump
    ? (seconds) => {
        if (seconds !== null && seconds !== undefined) {
          onJumpTo(seconds, obs.id != null ? obs.id : obs.group_id);
        }
      }
    : undefined;

  const register = registerFor(code);
  const entryId = obs.id != null ? obs.id : obs.group_id;
  // `code !== 'recurrence'` is checked here BY NAME, not only via `disposition`: a
  // recurrence entry is `disposition === 'finding'` (it was one, before being folded
  // away -- see this file's module docstring), so relying on disposition alone would let
  // a caller that supplies `onStatusChange` accidentally offer approve/reject on a
  // repeat. The decision belongs to the survivor entry it names.
  const canReview =
    obs.disposition === 'finding' && code !== 'recurrence' && typeof onStatusChange === 'function';

  const fullBody = (
    <>
      <FrameStrip frames={orderedFrames} activeIndex={activeFrameIndex} onSelect={setActiveFrameIndex} onJump={jump} />
      {code !== 'recurrence' && <ObservedBlock observed={obs.observed} />}
      <SpeechBlock speech={obs.speech} />
      <AssessmentBlock observation={obs} onJump={jump} videoKind={videoKind} />
      <OutcomeSection
        observation={obs}
        entryId={entryId}
        canReview={canReview}
        isEditing={isEditing}
        isSaving={isSaving}
        criteria={criteria}
        criteriaAvailable={criteriaAvailable}
        onStatusChange={onStatusChange}
        onEdit={onEdit}
        onCancelEdit={onCancelEdit}
        onSave={onSave}
      />
    </>
  );

  return (
    <article
      id={entryId ? `observation-${entryId}` : undefined}
      data-testid="observation-entry"
      data-outcome-code={code}
      className={`bg-white rounded-lg shadow-sm border p-6 break-inside-avoid ${
        isActive ? 'border-blue-400 ring-1 ring-blue-200' : 'border-gray-200'
      }`}
    >
      <EntryHeader observation={obs} register={register} videoKind={videoKind} />

      {quiet ? (
        <div className="space-y-3">
          <CompactPreview observation={obs} onJump={jump} />
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded((e) => !e)}
            className="print:hidden inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            {expanded ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )}
            {expanded ? 'Hide details' : 'Show details'}
          </button>
          {expanded && <div className="print:hidden space-y-4 pt-1">{fullBody}</div>}
        </div>
      ) : (
        <div className="space-y-4">{fullBody}</div>
      )}
    </article>
  );
}
